import { alpha, Box, Input, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ReactComponent as HorCen } from './icons/align-center-horizontal.svg';
import { ReactComponent as HorLef } from './icons/align-left.svg';
import { ReactComponent as HorRig } from './icons/align-right.svg';
import { ReactComponent as VerTop } from './icons/align-top.svg';
import { ReactComponent as VerBot } from './icons/align-bottom.svg';
import { ReactComponent as VerMid } from './icons/align-center-vertical.svg';
import { ReactComponent as VerStr } from './icons/align-ver-str.svg';
import { ReactComponent as HorStr } from './icons/align-hor-str.svg';
import { ReactComponent as HorSB } from './icons/align-sb.svg';
import { ReactComponent as HorSA } from './icons/hor-sa.svg';
import { ReactComponent as VerSB } from './icons/align-sb-ver.svg';
import { ReactComponent as VerSA } from './icons/ver-sa.svg';
import { ReactComponent as DirHor } from './icons/direction-hor.svg';
import { ReactComponent as DirVer } from './icons/direction-ver.svg';
import ColorField from 'app/app/editor/tabs/settings/style-editor/ColorField';
import FontField from 'app/app/editor/tabs/settings/style-editor/FontField';
import SizeField from 'app/app/editor/tabs/settings/style-editor/SizeField';
import styles from './StyleEditorContainer.styles';
import IndentPreview from 'app/app/editor/tabs/settings/style-editor/IndentPreview';
import FieldBySide from 'app/app/editor/tabs/settings/style-editor/FieldBySide';

import { IComponentNode } from 'app/app/editor/index.functions';
import { useState } from 'react';
import IconMi from 'components/common/icon/IconMi';
interface StylesEditorContainerProps {
    formState: any;
    setFormState: (value: any) => void;
    onChangeStyle: (value: any) => void;
    uid: string;
    node: IComponentNode;
}

const defaultPropertyList: any = {
    'flex-direction': {
        default: 'column',
    },
    'justify-content': {
        default: 'flex-start',
    },
    'align-items': {
        default: 'stretch',
    },
    'justify-self': {
        default: '',
    },
    'align-self': {
        default: '',
    },
    width: {
        default: '',
    },
    height: {
        default: '',
    },
    margin: {
        default: '',
    },
    padding: {
        default: '',
    },
    'background-color': {
        default: '',
    },
    'border-color': {
        default: '',
    },
    'border-radius': {
        default: '',
    },
    'border-width': {
        default: '',
    },
    resizable: {
        default: 'off',
    },
    'font-size': {
        default: '12px',
    },
    'font-family': {
        default: '',
    },
    color: {
        default: '',
    },
    'size-type-width': {
        default: 'fill',
    },
    'size-type-height': {
        default: 'fill',
    },
    'background-image': {
        default: '',
    },
    'background-size': {
        default: '',
    },
    'background-repeat': {
        default: '',
    },
};

export const getAppBuilderCurrentStyleValue = (formState: any, propertyName: string) => {
    return undefined === formState[propertyName]
        ? defaultPropertyList?.[propertyName]?.default
        : 'object' === typeof formState[propertyName]
          ? JSON.stringify(formState[propertyName])
          : formState[propertyName];
};

const StyleSection = ({
    alias,
    collapsedSection,
    toggleCollapseState,
    title,
    children,
}: {
    alias: string;
    collapsedSection: any;
    toggleCollapseState: (alias: string) => void;
    title: string;
    children: any;
}) => {
    return (
        <Stack sx={styles.section} spacing={1} className={collapsedSection?.[alias] ? 'section--collapsed' : ''}>
            <Box
                sx={styles.sectionTitle}
                onClick={() => {
                    toggleCollapseState(alias);
                }}
            >
                <Box>{title}</Box>
                <Box sx={styles.sectionTitleIcon}>
                    <IconMi icon={collapsedSection?.[alias] ? 'chevron-right' : 'chevron-down'} />
                </Box>
            </Box>
            <Stack sx={styles.sectionControlsContainer} spacing={1}>
                {children}
            </Stack>
        </Stack>
    );
};
export default function StylesEditorContainer({
    formState,
    setFormState = () => {},
    onChangeStyle = () => {},
    uid,
    node,
}: StylesEditorContainerProps) {
    const isMenuContainer = node.subtype == 'page_menu';
    const [collapsedSection, setCollapsedSection] = useState<{ [key: string]: boolean }>({
        spacing: true,
        border: true,
        background: true,
        text: true,
    });
    const getTextField = (propertyName: string, placeholderValue?: string) => {
        return (
            <Input
                fullWidth
                value={getAppBuilderCurrentStyleValue(formState, propertyName)}
                onChange={(e) => {
                    onChangeStyle({ [propertyName]: e.target.value });
                }}
                placeholder={placeholderValue ?? ''}
            />
        );
    };

    const toggleCollapseState = (sectionName: string) => {
        setCollapsedSection({ ...collapsedSection, [sectionName]: !collapsedSection?.[sectionName] });
    };

    return (
        <Stack spacing={2}>
            <StyleSection
                alias={'size'}
                title={'Size'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Width</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <SizeField
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            propertyName={'width'}
                            uid={uid}
                        />
                    </Box>
                </Box>
                {!isMenuContainer && (
                    <Box sx={styles.propertyContainer}>
                        <Box sx={styles.propertyTitle}>Height</Box>
                        <Box sx={styles.propertyControlHolder}>
                            <SizeField
                                formState={formState}
                                onChangeStyle={onChangeStyle}
                                propertyName={'height'}
                                uid={uid}
                            />
                        </Box>
                    </Box>
                )}
            </StyleSection>

            <StyleSection
                alias={'layout'}
                title={'Layout'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                {isMenuContainer ? (
                    <>
                        <Box>
                            <Box>Dockable</Box>
                            <ToggleButtonGroup
                                value={getAppBuilderCurrentStyleValue(formState, 'left') == 'auto' ? 'right' : 'left'}
                                exclusive
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        onChangeStyle({
                                            left: value == 'left' ? 0 : 'auto',
                                            right: value == 'left' ? 'auto' : 0,
                                        });
                                    }
                                }}
                            >
                                <ToggleButton value="left">Left</ToggleButton>
                                <ToggleButton value="right">Right</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box>
                            <Box>Resizable</Box>
                            <ToggleButtonGroup
                                value={getAppBuilderCurrentStyleValue(formState, 'resize') ?? 'horizontal'}
                                exclusive
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        onChangeStyle({ resize: value });
                                    }
                                }}
                            >
                                <ToggleButton value="horizontal">Yes</ToggleButton>
                                <ToggleButton value="none">No</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box>
                            <Box>Collapse with</Box>

                            <ToggleButtonGroup
                                value={getAppBuilderCurrentStyleValue(formState, 'menu-collapse-type') ?? 'x'}
                                exclusive
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        onChangeStyle({ 'menu-collapse-type': value });
                                    }
                                }}
                            >
                                <ToggleButton value="x">X</ToggleButton>
                                <ToggleButton value="arrow">{'<<'}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </>
                ) : (
                    <Box sx={styles.propertyContainer}>
                        <Box sx={styles.propertyTitle}>Direction</Box>
                        <Box sx={styles.propertyControlHolder}>
                            <ToggleButtonGroup
                                value={getAppBuilderCurrentStyleValue(formState, 'flex-direction')}
                                exclusive
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        onChangeStyle({ 'flex-direction': value });
                                    }
                                }}
                                sx={{
                                    svg: { width: '16px', height: '16px' },
                                }}
                            >
                                <ToggleButton value="row">
                                    <DirHor /> <Box sx={{ ml: 1 }}>Horizontal</Box>
                                </ToggleButton>
                                <ToggleButton value="column">
                                    <DirVer /> <Box sx={{ ml: 1 }}>Vertical</Box>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                )}
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>
                        Align children by{' '}
                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'X' : 'Y'}
                    </Box>
                    <Box sx={styles.propertyControlHolder}>
                        <ToggleButtonGroup
                            value={getAppBuilderCurrentStyleValue(formState, 'justify-content')}
                            exclusive
                            onChange={(event, value) => {
                                if (value !== null) {
                                    onChangeStyle({ 'justify-content': value });
                                }
                            }}
                            sx={{
                                svg: { width: '16px', height: '16px' },
                                '.Mui-selected': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            <ToggleButton value="flex-start">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <HorLef />
                                ) : (
                                    <VerTop />
                                )}
                            </ToggleButton>
                            <ToggleButton value="center">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <HorCen />
                                ) : (
                                    <VerMid />
                                )}
                            </ToggleButton>
                            <ToggleButton value="flex-end">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <HorRig />
                                ) : (
                                    <VerBot />
                                )}
                            </ToggleButton>
                            <ToggleButton value="space-around">
                                {' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <HorSA />
                                ) : (
                                    <VerSA />
                                )}
                            </ToggleButton>
                            <ToggleButton value="space-between">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <HorSB />
                                ) : (
                                    <VerSB />
                                )}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>
                        Align children by{' '}
                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'Y' : 'X'}
                    </Box>
                    <Box sx={styles.propertyControlHolder}>
                        <ToggleButtonGroup
                            value={getAppBuilderCurrentStyleValue(formState, 'align-items')}
                            exclusive
                            onChange={(event, value) => {
                                if (value !== null) {
                                    onChangeStyle({ 'align-items': value });
                                }
                            }}
                            sx={{
                                svg: { width: '16px', height: '16px' },
                                '.Mui-selected': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            <ToggleButton value="flex-start">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <VerTop />
                                ) : (
                                    <HorLef />
                                )}
                            </ToggleButton>
                            <ToggleButton value="center">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <VerMid />
                                ) : (
                                    <HorCen />
                                )}
                            </ToggleButton>
                            <ToggleButton value="flex-end">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <VerBot />
                                ) : (
                                    <HorRig />
                                )}
                            </ToggleButton>
                            <ToggleButton value="stretch">
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                    <VerStr />
                                ) : (
                                    <HorStr />
                                )}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
                {!isMenuContainer && (
                    <>
                        <Box sx={styles.propertyContainer}>
                            <Box sx={styles.propertyTitle}>
                                Align self by{' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'X' : 'Y'}
                            </Box>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    value={getAppBuilderCurrentStyleValue(formState, 'justify-self')}
                                    exclusive
                                    onChange={(event, value) => {
                                        onChangeStyle({ 'justify-self': value ?? '' });
                                    }}
                                    sx={{
                                        svg: { width: '16px', height: '16px' },
                                        '.Mui-selected': {
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                        },
                                    }}
                                >
                                    <ToggleButton value="flex-start">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <HorLef />
                                        ) : (
                                            <VerTop />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <HorCen />
                                        ) : (
                                            <VerMid />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="flex-end">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <HorRig />
                                        ) : (
                                            <VerBot />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="space-around">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <HorSA />
                                        ) : (
                                            <VerSA />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="space-between">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <HorSB />
                                        ) : (
                                            <VerSB />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        <Box sx={styles.propertyContainer}>
                            <Box sx={styles.propertyTitle}>
                                Align self by{' '}
                                {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? 'Y' : 'X'}
                            </Box>
                            <Box sx={styles.propertyControlHolder}>
                                <ToggleButtonGroup
                                    value={getAppBuilderCurrentStyleValue(formState, 'align-self')}
                                    exclusive
                                    onChange={(event, value) => {
                                        onChangeStyle({ 'align-self': value ?? '' });
                                    }}
                                    sx={{
                                        svg: { width: '16px', height: '16px' },
                                        '.Mui-selected': {
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                        },
                                    }}
                                >
                                    <ToggleButton value="flex-start">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <VerTop />
                                        ) : (
                                            <HorLef />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="center">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <VerMid />
                                        ) : (
                                            <HorCen />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="flex-end">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <VerBot />
                                        ) : (
                                            <HorRig />
                                        )}
                                    </ToggleButton>
                                    <ToggleButton value="stretch">
                                        {getAppBuilderCurrentStyleValue(formState, 'flex-direction') == 'row' ? (
                                            <VerStr />
                                        ) : (
                                            <HorStr />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </>
                )}
            </StyleSection>

            <StyleSection
                alias={'spacing'}
                title={'Spacing'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box>
                    <IndentPreview formState={formState} uid={uid} onChangeStyle={onChangeStyle} />
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Margin</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <FieldBySide
                            type={'side'}
                            propertyName={'margin'}
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            uid={uid}
                        />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Padding</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <FieldBySide
                            type={'side'}
                            propertyName={'padding'}
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            uid={uid}
                        />
                    </Box>
                </Box>
            </StyleSection>

            <StyleSection
                alias={'border'}
                title={'Border'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Border Width</Box>
                    <Box sx={styles.propertyControlHolder}>{getTextField('border-width', '0px')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Border Radius</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <FieldBySide
                            type={'corner'}
                            propertyName={'border-radius'}
                            formState={formState}
                            onChangeStyle={onChangeStyle}
                            uid={uid}
                        />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Border Color</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <ColorField
                            propertyName={'border-color'}
                            onChangeStyle={onChangeStyle}
                            formState={formState}
                            uid={uid}
                        />
                    </Box>
                </Box>
            </StyleSection>

            <StyleSection
                alias={'background'}
                title={'Background'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Background Color</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <ColorField
                            propertyName={'background-color'}
                            onChangeStyle={onChangeStyle}
                            formState={formState}
                            uid={uid}
                        />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Background Image</Box>
                    <Box sx={styles.propertyControlHolder}>{getTextField('background-image')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Background Size</Box>
                    <Box sx={styles.propertyControlHolder}>{getTextField('background-size')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Background Repeat</Box>
                    <Box sx={styles.propertyControlHolder}>{getTextField('background-repeat')}</Box>
                </Box>
            </StyleSection>

            <StyleSection
                alias={'text'}
                title={'Text'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Font</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <FontField propertyName={'font-family'} onChangeStyle={onChangeStyle} formState={formState} />
                    </Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Font Size</Box>
                    <Box sx={styles.propertyControlHolder}>{getTextField('font-size')}</Box>
                </Box>
                <Box sx={styles.propertyContainer}>
                    <Box sx={styles.propertyTitle}>Color</Box>
                    <Box sx={styles.propertyControlHolder}>
                        <ColorField
                            propertyName={'color'}
                            onChangeStyle={onChangeStyle}
                            formState={formState}
                            uid={uid}
                        />
                    </Box>
                </Box>
            </StyleSection>

            {/*
            <StyleSection
                alias={'spacing'}
                title={'Spacing'}
                collapsedSection={collapsedSection}
                toggleCollapseState={toggleCollapseState}
            >
            <Box>Section content</Box>
            </StyleSection>
            */}
        </Stack>
    );
}
